import { useState, useEffect } from "react";
import Icon from "../../assets/icons/Icon";

const center = 150;
const outerRadius = 150;
const innerRadius = 30;
const separatorInner = 75;
const separatorOuter = 105;

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

function describeArc(cx, cy, innerR, outerR, startAngle, endAngle) {
  const startOuter = polarToCartesian(cx, cy, outerR, startAngle);
  const endOuter = polarToCartesian(cx, cy, outerR, endAngle);
  const startInner = polarToCartesian(cx, cy, innerR, endAngle);
  const endInner = polarToCartesian(cx, cy, innerR, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${outerR} ${outerR} 0 ${largeArcFlag} 1 ${endOuter.x} ${endOuter.y}`,
    `L ${startInner.x} ${startInner.y}`,
    `A ${innerR} ${innerR} 0 ${largeArcFlag} 0 ${endInner.x} ${endInner.y}`,
    "Z",
  ].join(" ");
}

const UnitControlMenu = ({ title, menuOptions, selectedOption, setSelectedOption, selectedMode }) => {
  const segmentCount = menuOptions.length;
  const segmentAngle = 360 / segmentCount;
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
      let disabled = false;
  
      if (selectedMode === "DRY") {
        disabled = true;
      } else {
        disabled = false;
      }
  
      setIsDisabled(disabled);
    }, [selectedMode]);

  return (
    <div
      className={`w-full max-w-xs sm:max-w-sm md:max-w-md mx-auto ${isDisabled ? "opacity-50 pointer-events-none" : "opacity-100"}`}
      data-testid={`unit-control-menu-${title}`}
    >
      <svg
        viewBox="0 0 300 300"
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Outer Circle Border */}
        <circle
          cx={center}
          cy={center}
          r={outerRadius}
          fill="none"
          stroke="#999"
          strokeWidth={1}
        />

        {menuOptions.map((mode, index) => {
          const startAngle = index * segmentAngle;
          const endAngle = startAngle + segmentAngle;

          const path = describeArc(
            center,
            center,
            innerRadius,
            outerRadius,
            startAngle,
            endAngle
          );

          const isSelected = selectedOption === mode.value;

          const midAngle = startAngle + segmentAngle / 2;
          const labelPos = polarToCartesian(
            center,
            center,
            (innerRadius + outerRadius) / 2,
            midAngle
          );

          const lineStart = polarToCartesian(
            center,
            center,
            separatorInner,
            endAngle
          );
          const lineEnd = polarToCartesian(
            center,
            center,
            separatorOuter,
            endAngle
          );

          return (
            <g key={mode.id}>
              <path
                d={path}
                fill={isSelected ? "#1f555c" : "transparent"}
                stroke="none"
                onClick={() => setSelectedOption(mode.value)}
                style={{ cursor: "pointer" }}
                data-testid={`unit-control-menu-${title}-option-${mode.value}`}
              />
              <line
                x1={lineStart.x}
                y1={lineStart.y}
                x2={lineEnd.x}
                y2={lineEnd.y}
                stroke="#999"
                strokeWidth={1}
              />

              {/* Icon or Number */}
              {mode?.number ? (
                <>
                  <text
                    x={labelPos.x}
                    y={labelPos.y - 10}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="18"
                    fontWeight="bold"
                    fill={isSelected ? "#ffffff" : "#333 dark:text-gray-800"}
                    pointerEvents="none"
                  >
                    {mode.number}
                  </text>
                  <text
                    x={labelPos.x}
                    y={labelPos.y + 12}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="10"
                    fontWeight="bold"
                    fill={isSelected ? "#ffffff" : "#333 dark:text-gray-800"}
                    pointerEvents="none"
                  >
                    {mode.label}
                  </text>
                </>
              ) : (
                <>
                  <foreignObject
                    x={labelPos.x - 25}
                    y={labelPos.y - 35}
                    width={50}
                    height={50}
                    pointerEvents="none"
                  >
                    <Icon
                      src={mode.icon}
                      alt={mode.label + "Icon"}
                      className="w-full h-full"
                    />
                  </foreignObject>
                  <text
                    x={labelPos.x}
                    y={labelPos.y + 22}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="10"
                    fontWeight="bold"
                    fill={isSelected ? "#ffffff" : "#333 dark:text-gray-800"}
                    pointerEvents="none"
                  >
                    {mode.label}
                  </text>
                </>
              )}
            </g>
          );
        })}

        {/* Center Circle */}
        <circle cx={center} cy={center} r={40} fill="#fff" stroke="#999" />
        <text
          x={center}
          y={center}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="12"
          fontWeight="bold"
        >
          {title.toUpperCase()}
        </text>
      </svg>
    </div>
  );
};

export default UnitControlMenu;
